import React from 'react';
import styled from 'styled-components';

const Panel = styled.div`
  border: 1px solid var(--gray-200, #e5e7eb);
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0.75rem;
  background: var(--gray-50, #f9fafb);
  border-bottom: 1px solid var(--gray-200, #e5e7eb);
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--gray-700, #374151);
`;

const AddRootButton = styled.button`
  border: none;
  background: transparent;
  color: var(--primary-color, #2563eb);
  cursor: pointer;
  font-size: 0.8rem;
  padding: 0;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.5rem;
  padding-left: calc(0.5rem + ${props => props.$depth * 14}px);
  cursor: pointer;
  font-size: 0.85rem;
  color: ${props => (props.$active ? '#1d4ed8' : 'var(--gray-700, #374151)')};
  background: ${props => (props.$active ? '#eff6ff' : 'transparent')};
  font-weight: ${props => (props.$active ? '600' : '400')};

  &:hover {
    background: ${props => (props.$active ? '#eff6ff' : '#f3f4f6')};
    .tree-actions { opacity: 1; }
  }
`;

const Name = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Count = styled.span`
  color: #9ca3af;
  font-size: 0.72rem;
  flex-shrink: 0;
`;

const Actions = styled.span`
  display: flex;
  gap: 0.15rem;
  opacity: 0;
  transition: opacity 0.15s;
  flex-shrink: 0;
`;

const IconButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 0.75rem;
  padding: 0 0.15rem;
  color: #6b7280;
  line-height: 1;

  &:hover { color: ${props => (props.$danger ? '#dc2626' : '#2563eb')}; }
`;

const Toggle = styled.span`
  width: 12px;
  flex-shrink: 0;
  color: #9ca3af;
  font-size: 0.7rem;
  text-align: center;
`;

const KnowledgeTree = ({
  tree,
  unclassified = 0,
  selected,
  onSelect,
  expanded = {},
  onToggle,
  onCreate,
  onRename,
  onDelete
}) => {
  const renderNodes = (nodes, depth) => (nodes || []).map((node) => {
    const hasChildren = (node.children || []).length > 0;
    const isOpen = !!expanded[node.id];

    return (
      <div key={node.id}>
        <Row
          $depth={depth}
          $active={String(selected) === String(node.id)}
          onClick={() => onSelect(node.id)}
        >
          <Toggle
            onClick={(e) => {
              e.stopPropagation();
              if (hasChildren) onToggle(node.id);
            }}
          >
            {hasChildren ? (isOpen ? '▾' : '▸') : ''}
          </Toggle>
          <span>{hasChildren ? (isOpen ? '📂' : '📁') : '📄'}</span>
          <Name title={node.name}>{node.name}</Name>
          <Count>{node.totalCount}</Count>
          <Actions className="tree-actions">
            <IconButton
              title="新建子目录"
              onClick={(e) => { e.stopPropagation(); onCreate(node.id); }}
            >
              ＋
            </IconButton>
            <IconButton
              title="重命名"
              onClick={(e) => { e.stopPropagation(); onRename(node); }}
            >
              ✎
            </IconButton>
            <IconButton
              title="删除目录"
              $danger
              onClick={(e) => { e.stopPropagation(); onDelete(node); }}
            >
              ✕
            </IconButton>
          </Actions>
        </Row>
        {isOpen && hasChildren && renderNodes(node.children, depth + 1)}
      </div>
    );
  });

  return (
    <Panel>
      <PanelHeader>
        <span>📚 知识目录</span>
        <AddRootButton onClick={() => onCreate(null)}>+ 根目录</AddRootButton>
      </PanelHeader>

      <div style={{ padding: '0.25rem 0' }}>
        <Row $depth={0} $active={selected === 'all'} onClick={() => onSelect('all')}>
          <Toggle />
          <span>🌟</span>
          <Name>全部知识</Name>
        </Row>

        <Row $depth={0} $active={selected === 'none'} onClick={() => onSelect('none')}>
          <Toggle />
          <span>📥</span>
          <Name>未分类</Name>
          <Count>{unclassified}</Count>
        </Row>

        {(tree || []).length === 0 ? (
          <div style={{ padding: '0.75rem', fontSize: '0.8rem', color: '#9ca3af' }}>
            还没有目录，点右上角「+ 根目录」新建
          </div>
        ) : (
          renderNodes(tree, 0)
        )}
      </div>
    </Panel>
  );
};

export default KnowledgeTree;
